/**
 * Stripe Client Initialization (Frontend)
 * Feature: 001-vi-m-ste
 * Purpose: Initialize Stripe.js client with environment-specific publishable keys
 * Author: Claude Code
 * Date: 2025-10-25
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { logger } from '@/utils/logger';

/**
 * Get Stripe publishable key for current environment
 *
 * @throws {Error} If publishable key is missing or invalid
 * @returns {string} Stripe publishable key
 */
function getStripePublishableKey(): string {
  const env = import.meta.env.VITE_APP_ENV || 'staging';

  // Select appropriate key based on environment
  const key = env === 'production'
    ? import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_LIVE
    : import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_TEST;

  if (!key) {
    throw new Error(
      `Missing Stripe publishable key for ${env} environment. ` +
      `Required: VITE_STRIPE_PUBLISHABLE_KEY_${env === 'production' ? 'LIVE' : 'TEST'}`
    );
  }

  // Validate key format matches environment
  const isTestKey = key.startsWith('pk_test_');
  const isLiveKey = key.startsWith('pk_live_');

  if (!isTestKey && !isLiveKey) {
    throw new Error(
      `Invalid Stripe publishable key format. Expected pk_test_* or pk_live_*, got: ${key.substring(0, 10)}...`
    );
  }

  // Prevent test keys in production (FR-013)
  if (env === 'production' && !isLiveKey) {
    throw new Error(
      'CRITICAL: Production must use Stripe live publishable key (pk_live_*). ' +
      'Found test key instead. This violates FR-013.'
    );
  }

  // Prevent live keys in staging (FR-013)
  if (env === 'staging' && !isTestKey) {
    throw new Error(
      'CRITICAL: Staging must use Stripe test publishable key (pk_test_*). ' +
      'Found live key instead. This violates FR-013.'
    );
  }

  logger.debug(`[Stripe Client] Environment: ${env}, Key type: ${isTestKey ? 'TEST' : 'LIVE'} ✓`);

  return key;
}

/**
 * Stripe.js instance (singleton pattern)
 *
 * Initialized lazily on first access to avoid blocking app startup.
 * Uses loadStripe() which loads Stripe.js asynchronously from Stripe's CDN.
 */
let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Get Stripe.js instance
 *
 * Returns a promise that resolves to the Stripe object.
 * Subsequent calls return the same promise (singleton pattern).
 *
 * @returns {Promise<Stripe | null>} Stripe.js instance
 */
export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const publishableKey = getStripePublishableKey();
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

/**
 * Check if running in test mode
 *
 * @returns {boolean} True if using Stripe test credentials
 */
export function isTestMode(): boolean {
  const env = import.meta.env.VITE_APP_ENV || 'staging';
  return env !== 'production';
}

/**
 * Get current environment name
 *
 * @returns {string} 'staging' or 'production'
 */
export function getEnvironment(): 'staging' | 'production' {
  return import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'staging';
}
